import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { generateReport, listReportFormats, type GeneratedReport, type ReportFormat } from '../../api/reportsApi';
import ReportViewer from './ReportViewer';

/** "Generate report ▾" for a chat session: pick a format, wait, view, download. */

interface Props {
  sessionId: number;
  className?: string;
  disabled?: boolean;
  canUse: boolean;
  onUpgrade?: () => void;
}

const GenerateReportButton: React.FC<Props> = ({ sessionId, className, disabled, canUse, onUpgrade }) => {
  const [formats, setFormats] = useState<ReportFormat[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<GeneratedReport | null>(null);

  useEffect(() => { listReportFormats().then((r) => setFormats(r.formats)).catch(() => {}); }, []);

  const run = async (key: string) => {
    if (!canUse) { onUpgrade?.(); return; }
    setOpen(false); setBusy(key); setError(null);
    try { setReport(await generateReport(sessionId, key)); }
    catch (e) { setError(e instanceof Error ? e.message : 'The report could not be generated'); }
    finally { setBusy(null); }
  };

  const label = formats.find((f) => f.key === report?.format)?.label;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" className={className} disabled={disabled || !!busy} onClick={() => setOpen((o) => !o)}
              title={canUse ? 'Turn this conversation into a formal report' : 'Reports are a Professional feature — upgrade to unlock'} aria-haspopup="menu" aria-expanded={open}>
        <FileText size={16} /> {busy ? 'Generating…' : canUse ? 'Generate report ▾' : 'Generate report (Pro)'}
      </button>
      {open && (
        <ul role="menu" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20, listStyle: 'none', padding: 4, minWidth: 260, background: 'var(--ds-surface, #fff)', border: '1px solid var(--ds-border, var(--ds-border))', borderRadius: 10, boxShadow: '0 8px 24px rgba(16,24,40,0.12)' }}>
          {formats.map((f) => (
            <li key={f.key}>
              <button type="button" role="menuitem" onClick={() => run(f.key)} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', color: 'var(--color-text, var(--ds-text))' }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{f.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-muted, var(--ds-text-muted))' }}>for {f.audience}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <div role="alert" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, fontSize: 12, color: 'var(--ds-danger-text)', whiteSpace: 'nowrap' }}>{error}</div>}
      {report && <ReportViewer report={report} formatLabel={label} onClose={() => setReport(null)} />}
    </div>
  );
};

export default GenerateReportButton;
